/**
 * Notification bottom d'un indice trouvé/révélé. Positionnée en bas absolu
 * (fixed bottom + safe-area), auto-dismiss à 2 s. Le design MIRRORE celui
 * d'une ligne d'indice révélé dans la carte inline (fond emerald-50, badge
 * XP à droite) pour une cohérence visuelle : l'élève retrouve exactement le
 * même style que dans le bloc.
 */

import { useEffect } from 'react';
import { MarkdownMath, cn } from '@jeraisonne/ui';

export interface CluePopupProps {
  /** Ce qui s'affiche (KaTeX supporté). */
  revealedMd: string;
  /** XP effectivement crédités (0 = timeout / 3 mauvais clics). */
  xpEarned: number;
  /** Appelé après auto-dismiss (parent nettoie l'état). */
  onDismiss: () => void;
  /** Durée avant auto-dismiss (ms). Défaut 2000 ms. */
  durationMs?: number;
}

export function CluePopup({ revealedMd, xpEarned, onDismiss, durationMs = 2000 }: CluePopupProps) {
  useEffect(() => {
    const t = setTimeout(onDismiss, durationMs);
    return () => clearTimeout(t);
  }, [onDismiss, durationMs]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="animate-in slide-in-from-bottom-4 fade-in pointer-events-none fixed inset-x-0 bottom-0 z-[75] flex justify-center px-3 duration-200"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
    >
      <div className="flex w-full max-w-sm items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-sm shadow-lg">
        <span className="text-ink-900 min-w-0 flex-1 font-medium">
          <MarkdownMath>{revealedMd}</MarkdownMath>
        </span>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
            xpEarned > 0 ? 'bg-emerald-200 text-emerald-900' : 'bg-amber-200 text-amber-900',
          )}
        >
          {xpEarned > 0 ? `+${xpEarned}` : '0'}
        </span>
      </div>
    </div>
  );
}
